import { memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * NameCard — the big engraved name plate for a reveal (the lynched, the victim,
 * the winner). The name is set in Grenze at the --stage-text-hero scale with a
 * gold gradient fill, and stamps in: scaling from 1.15→1 while a 6px blur
 * resolves to sharp, on the --ease-dramatic curve — like a wax seal pressed home.
 *
 * `variant` tints the underline / accent:
 *   'condemned' — blood (the lynched)
 *   'victim'    — blood, softer (the night's kill)
 *   'neutral'   — gold (default, e.g. a winner headline)
 *
 * Reduced-motion skips the stamp (instant, no blur/scale).
 */

export type NameCardVariant = 'condemned' | 'victim' | 'neutral';

export interface NameCardProps {
  name: string;
  /** Small line above the name, e.g. "The village condemns" / "Slain in the night". */
  eyebrow?: string;
  variant?: NameCardVariant;
}

const UNDERLINE: Record<NameCardVariant, string> = {
  condemned: 'linear-gradient(90deg, transparent, var(--blood-5, #c22f3e), transparent)',
  victim: 'linear-gradient(90deg, transparent, var(--blood-3, #802636), transparent)',
  neutral: 'linear-gradient(90deg, transparent, var(--gold-5, #e6b866), transparent)',
};

const EYEBROW_COLOR: Record<NameCardVariant, string> = {
  condemned: 'var(--blood-6, #ff5a4f)',
  victim: 'var(--blood-5, #c22f3e)',
  neutral: 'var(--gold-5, #e6b866)',
};

function NameCardImpl({ name, eyebrow, variant = 'neutral' }: NameCardProps) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.15, filter: 'blur(6px)' }}
      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      transition={{ duration: reduced ? 0 : 0.55, ease: [0.7, 0, 0.16, 1] }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        textAlign: 'center',
      }}
    >
      {eyebrow && (
        <span
          style={{
            fontFamily: 'var(--font-ui, Inter, sans-serif)',
            fontSize: 'var(--stage-text-meta, 24px)',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: EYEBROW_COLOR[variant],
          }}
        >
          {eyebrow}
        </span>
      )}

      <span
        style={{
          fontFamily: 'var(--font-display, serif)',
          fontSize: 'var(--stage-text-hero, 144px)',
          lineHeight: 1,
          background: 'linear-gradient(180deg, var(--gold-6, #f7e3b0) 0%, var(--gold-3, #a9762f) 100%)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
          padding: '0 8px',
        }}
      >
        {name}
      </span>

      <div
        aria-hidden
        style={{
          width: 'min(60%, 480px)',
          height: 3,
          background: UNDERLINE[variant],
        }}
      />
    </motion.div>
  );
}

export const NameCard = memo(NameCardImpl);
